// Structured renderers for Ads AI, Social AI, and Reviews AI tool outputs.
// Falls back gracefully if the LLM markdown doesn't perfectly match.
import { Copy, InstagramLogo, LinkedinLogo, XLogo, Heart, ChatCircle, PaperPlaneTilt, BookmarkSimple, ThumbsUp, Repeat, ShareFat, DotsThreeOutline, Star } from "@phosphor-icons/react";
import { toast } from "sonner";

function copy(text) {
  navigator.clipboard.writeText(text);
  toast.success("Copied.");
}

// ---------- Shared helpers ----------
function splitBulletsBySection(markdown) {
  const sections = {};
  const lines = markdown.split("\n");
  let current = null;
  for (const raw of lines) {
    const line = raw.trim();
    const h = line.match(/^#{1,3}\s+(.*)$/);
    if (h) {
      current = h[1].replace(/\*\*/g, "").trim();
      sections[current] = [];
      continue;
    }
    const b = line.match(/^[-*•]\s+(.*)$/);
    if (b && current) sections[current].push(b[1].replace(/\*\*/g, "").trim());
  }
  return sections;
}

// ---------- Ads ----------
export function AdsRenderer({ output }) {
  const sections = splitBulletsBySection(output);
  const keys = Object.keys(sections);

  const find = (needle) => {
    const k = keys.find((x) => x.toLowerCase().includes(needle));
    return k ? sections[k] : [];
  };

  const findByPattern = (pattern) => {
    const matched = keys.filter((x) => pattern.test(x.toLowerCase()));
    return matched.flatMap((k) => sections[k] || []);
  };

  const headlines = find("headline");
  const descriptions = find("description");
  const metaText = find("meta");
  const cta = find("cta");
  const targeting = find("targeting");

  const extraSocialGroups = [
    { label: "Instagram · Ad Copy", items: findByPattern(/instagram|insta/) },
    { label: "LinkedIn · Ad Copy", items: findByPattern(/linkedin/) },
    { label: "X / Twitter · Ad Copy", items: findByPattern(/(^|\b)(x|twitter)(\b|$)/) },
    { label: "TikTok · Ad Copy", items: findByPattern(/tiktok|tik tok/) },
  ].filter((group) => group.items.length > 0);

  return (
    <div className="flex flex-col gap-6" data-testid="ads-renderer">
      <Group title="Google RSA · Headlines" hint="≤ 30 chars each">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {headlines.map((h, i) => (
            <AdCard key={i} label={`H${i + 1}`} text={h} limit={30} />
          ))}
        </div>
      </Group>

      <Group title="Google RSA · Descriptions" hint="≤ 90 chars each">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {descriptions.map((d, i) => (
            <AdCard key={i} label={`D${i + 1}`} text={d} limit={90} />
          ))}
        </div>
      </Group>

      <Group title="Meta · Primary Text" hint="≤ 125 chars each">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {metaText.map((m, i) => (
            <AdCard key={i} label={`M${i + 1}`} text={m} limit={125} />
          ))}
        </div>
      </Group>

      {extraSocialGroups.map((group) => (
        <Group key={group.label} title={group.label}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {group.items.map((item, i) => (
              <AdCard key={i} label={`${group.label.charAt(0)}${i + 1}`} text={item} />
            ))}
          </div>
        </Group>
      ))}

      {cta.length > 0 && (
        <Group title="Hero CTA">
          <div className="bg-[#002FA7] text-white p-5 flex items-center justify-between">
            <span className="font-display text-xl font-extrabold">{cta[0]}</span>
            <button className="text-sm font-semibold bg-white text-[#002FA7] px-3 py-1.5" onClick={() => copy(cta[0])}>
              Copy
            </button>
          </div>
        </Group>
      )}

      {targeting.length > 0 && (
        <Group title="Targeting suggestions">
          <ul className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {targeting.map((t, i) => (
              <li key={i} className="border border-[#E5E7EB] p-4 text-sm">
                {t}
              </li>
            ))}
          </ul>
        </Group>
      )}
    </div>
  );
}

function AdCard({ label, text, limit }) {
  const len = text.replace(/^"|"$/g, "").length;
  const over = limit && len > limit;
  return (
    <div className="border border-[#E5E7EB] bg-white p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#737373]">{label}</span>
        {limit && (
          <span className={`font-mono text-[10px] ${over ? "text-[#DC2626]" : "text-[#737373]"}`}>
            {len}/{limit}
          </span>
        )}
      </div>
      <p className="text-[15px] leading-snug flex-1">{text}</p>
      <button onClick={() => copy(text)} className="text-xs font-semibold text-[#002FA7] inline-flex items-center gap-1 self-start">
        <Copy size={12} weight="bold" /> Copy
      </button>
    </div>
  );
}

// ---------- Social ----------
function detectPlatform(raw) {
  const s = String(raw || "").toLowerCase();
  if (s.includes("insta")) return "instagram";
  if (s.includes("linkedin") || s.includes("li ")) return "linkedin";
  if (/\b(x|twitter)\b/.test(s)) return "x";
  if (s.includes("tiktok")) return "tiktok";
  if (s.includes("facebook") || s.includes("fb")) return "facebook";
  return "generic";
}

export function SocialRenderer({ output, inputs = {} }) {
  const blocks = output.split(/\n-{3,}\n/).map((b) => b.trim()).filter(Boolean);
  const posts = blocks.map(parsePost).filter((p) => p.hook || p.body);
  const platform = detectPlatform(inputs.platform);
  const brand = inputs.brand || "Your Brand";

  if (posts.length === 0) return null;

  return (
    <div className="flex flex-col gap-6" data-testid="social-renderer">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#737373]">
          Preview · {platform === "generic" ? "cards" : platform}
        </p>
        <span className="font-mono text-[10px] text-[#737373]">{posts.length} posts</span>
      </div>

      <div className={`grid gap-5 ${platform === "x" ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1 md:grid-cols-2"}`}>
        {posts.map((p, i) => (
          <PlatformPost key={i} post={p} index={i} platform={platform} brand={brand} />
        ))}
      </div>
    </div>
  );
}

function PlatformPost({ post, index, platform, brand }) {
  const fullCopy = `${post.hook ? post.hook + "\n\n" : ""}${post.body || ""}${post.hashtags ? "\n\n" + post.hashtags : ""}`.trim();
  const handle = "@" + brand.toLowerCase().replace(/[^a-z0-9]+/g, "").slice(0, 20);
  const initials = brand.split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();

  if (platform === "instagram") {
    return (
      <div className="border border-[#DBDBDB] bg-white overflow-hidden font-[system-ui,sans-serif]" data-testid={`social-post-${index}`}>
        <div className="flex items-center justify-between px-3 py-2.5 border-b border-[#EFEFEF]">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-gradient-to-tr from-[#FCAF45] via-[#E1306C] to-[#5851DB] flex items-center justify-center text-white text-xs font-bold">
              {initials}
            </div>
            <div className="flex flex-col leading-tight">
              <span className="text-[13px] font-semibold text-[#262626]">{brand.toLowerCase().replace(/\s+/g, "")}</span>
              <span className="text-[11px] text-[#8E8E8E]">Sponsored</span>
            </div>
          </div>
          <DotsThreeOutline size={18} weight="bold" className="text-[#262626]" />
        </div>
        <div className="aspect-square bg-gradient-to-br from-[#FCAF45]/20 via-[#E1306C]/15 to-[#5851DB]/20 flex items-center justify-center p-6">
          <p className="font-display text-xl sm:text-2xl font-black text-[#262626] text-center leading-tight">
            {post.hook || "Post preview"}
          </p>
        </div>
        <div className="px-3 py-2.5 flex items-center gap-4">
          <Heart size={24} weight="regular" className="text-[#262626]" />
          <ChatCircle size={22} weight="regular" className="text-[#262626]" />
          <PaperPlaneTilt size={22} weight="regular" className="text-[#262626]" />
          <BookmarkSimple size={22} weight="regular" className="ml-auto text-[#262626]" />
        </div>
        <div className="px-3 pb-3 text-[13px] text-[#262626]">
          <p className="whitespace-pre-wrap leading-snug">
            <span className="font-semibold">{brand.toLowerCase().replace(/\s+/g, "")}</span> {post.body}
          </p>
          {post.hashtags && <p className="mt-1.5 text-[#00376B]">{post.hashtags}</p>}
          {post.time && <p className="mt-2 text-[10px] uppercase tracking-wide text-[#8E8E8E]">Best time · {post.time}</p>}
        </div>
        <PostFooter onCopy={() => copy(fullCopy)} label={post.title || `Post ${index + 1}`} accent="#E1306C" />
      </div>
    );
  }

  if (platform === "linkedin") {
    return (
      <div className="border border-[#E0E0E0] bg-white font-[system-ui,sans-serif]" data-testid={`social-post-${index}`}>
        <div className="flex items-start gap-3 p-4 pb-2">
          <div className="h-12 w-12 bg-[#0A66C2] text-white flex items-center justify-center font-bold text-sm">
            {initials}
          </div>
          <div className="flex-1">
            <p className="text-[14px] font-semibold text-[#191919] leading-tight">{brand}</p>
            <p className="text-[12px] text-[#666666] leading-tight">{post.time || "Just now"} · 🌐</p>
          </div>
          <DotsThreeOutline size={20} weight="bold" className="text-[#666666]" />
        </div>
        <div className="px-4 pb-3">
          {post.hook && <p className="font-semibold text-[15px] text-[#191919] leading-snug mb-2">{post.hook}</p>}
          {post.body && <p className="text-[14px] text-[#191919] whitespace-pre-wrap leading-relaxed">{post.body}</p>}
          {post.hashtags && <p className="mt-2 text-[14px] text-[#0A66C2] font-medium">{post.hashtags}</p>}
        </div>
        <div className="border-t border-[#E0E0E0] flex items-center justify-around px-2 py-1.5 text-[12px] text-[#666666] font-semibold">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5"><ThumbsUp size={16} weight="regular" /> Like</span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5"><ChatCircle size={16} weight="regular" /> Comment</span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5"><Repeat size={16} weight="regular" /> Repost</span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5"><PaperPlaneTilt size={16} weight="regular" /> Send</span>
        </div>
        <PostFooter onCopy={() => copy(fullCopy)} label={post.title || `Post ${index + 1}`} accent="#0A66C2" />
      </div>
    );
  }

  if (platform === "x") {
    return (
      <div className="border border-[#E5E7EB] bg-white font-[system-ui,sans-serif]" data-testid={`social-post-${index}`}>
        <div className="flex gap-3 p-4">
          <div className="h-10 w-10 rounded-full bg-[#0A0A0A] text-white flex items-center justify-center font-bold text-xs shrink-0">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 text-[14px]">
              <span className="font-bold text-[#0F1419]">{brand}</span>
              <span className="text-[#536471]">{handle}</span>
              <span className="text-[#536471]">· {post.time || "now"}</span>
            </div>
            {post.hook && <p className="mt-1 text-[15px] text-[#0F1419] font-semibold leading-snug">{post.hook}</p>}
            {post.body && <p className="mt-1 text-[15px] text-[#0F1419] whitespace-pre-wrap leading-snug">{post.body}</p>}
            {post.hashtags && <p className="mt-1 text-[15px] text-[#1D9BF0]">{post.hashtags}</p>}
            <div className="mt-3 flex items-center justify-between max-w-md text-[13px] text-[#536471]">
              <span className="inline-flex items-center gap-1.5"><ChatCircle size={16} /> 12</span>
              <span className="inline-flex items-center gap-1.5"><Repeat size={16} /> 48</span>
              <span className="inline-flex items-center gap-1.5"><Heart size={16} /> 231</span>
              <span className="inline-flex items-center gap-1.5"><ShareFat size={16} /></span>
            </div>
          </div>
        </div>
        <PostFooter onCopy={() => copy(fullCopy)} label={post.title || `Post ${index + 1}`} accent="#1D9BF0" />
      </div>
    );
  }

  // Generic
  return (
    <div className="border border-[#E5E7EB] bg-white p-5 flex flex-col gap-3" data-testid={`social-post-${index}`}>
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#002FA7]">
          {post.title || `Post ${index + 1}`}
        </span>
        <button onClick={() => copy(fullCopy)} className="text-xs text-[#525252] font-semibold inline-flex items-center gap-1">
          <Copy size={12} weight="bold" /> Copy
        </button>
      </div>
      {post.hook && <p className="font-display text-lg font-bold leading-snug">{post.hook}</p>}
      {post.body && <p className="text-sm text-[#525252] whitespace-pre-wrap leading-relaxed">{post.body}</p>}
      {post.hashtags && <p className="text-xs font-mono text-[#002FA7]">{post.hashtags}</p>}
      {post.time && (
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#737373] border-t border-[#E5E7EB] pt-2">
          Best time · {post.time}
        </p>
      )}
    </div>
  );
}

function PostFooter({ onCopy, label, accent }) {
  return (
    <div className="border-t border-[#E5E7EB] bg-[#FAFAFA] px-3 py-2 flex items-center justify-between">
      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373]">{label}</span>
      <button
        onClick={onCopy}
        className="text-[11px] font-semibold inline-flex items-center gap-1 hover:opacity-70"
        style={{ color: accent }}
      >
        <Copy size={11} weight="bold" /> Copy caption
      </button>
    </div>
  );
}

function parsePost(block) {
  const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
  const obj = { title: "", hook: "", body: "", hashtags: "", time: "" };
  for (const line of lines) {
    const h = line.match(/^#{1,3}\s+(.*)$/);
    if (h) { obj.title = h[1].replace(/\*\*/g, ""); continue; }
    const kv = line.match(/^\*\*([^:]+):\*\*\s*(.*)$/);
    if (kv) {
      const k = kv[1].toLowerCase();
      const v = kv[2];
      if (k.includes("hook")) obj.hook = v;
      else if (k.includes("body")) obj.body = v;
      else if (k.includes("hashtag")) obj.hashtags = v;
      else if (k.includes("time")) obj.time = v;
      continue;
    }
    if (obj.body) obj.body += "\n" + line;
  }
  return obj;
}

// ---------- Reviews ----------
export function ReviewsRenderer({ output, inputs = {} }) {
  const business = inputs.business || "Your Business";
  const customerName = inputs.customer_name || inputs.customer || inputs.name || "Customer";
  const ratingValue = Math.max(1, Math.min(5, Number.parseFloat(inputs.rating || inputs.stars || 5) || 5));
  const review = inputs.review || "";
  const tone = inputs.tone || "professional";
  const reply = (output || "").trim();
  const initials = business.split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const customerInitials = customerName.split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div className="flex flex-col gap-5" data-testid="reviews-renderer">
      {review && (
        <div className="border border-[#E5E7EB] bg-[#FAFAFA] p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-full bg-[#E5E7EB] text-[#525252] flex items-center justify-center font-bold text-xs">
                {customerInitials || "C"}
              </div>
              <div className="leading-tight">
                <p className="text-[13px] font-semibold text-[#0A0A0A]">{customerName}</p>
                <div className="flex items-center gap-0.5 mt-0.5">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star
                      key={n}
                      size={12}
                      weight={n <= Math.round(ratingValue) ? "fill" : "regular"}
                      className={n <= Math.round(ratingValue) ? "text-[#F5A623]" : "text-[#A3A3A3]"}
                    />
                  ))}
                  <span className="ml-1 font-mono text-[10px] text-[#737373]">{ratingValue}/5</span>
                </div>
              </div>
            </div>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#737373]">Incoming</span>
          </div>
          <p className="text-[14px] text-[#525252] whitespace-pre-wrap leading-relaxed">{review}</p>
        </div>
      )}

      <div className="border-l-4 border-[#002FA7] bg-white pl-5 pr-5 py-5 md:ml-10 shadow-sm">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 bg-[#002FA7] text-white flex items-center justify-center font-bold text-xs">
              {initials}
            </div>
            <div className="leading-tight">
              <p className="text-[13px] font-semibold text-[#0A0A0A]">{business}</p>
              <p className="text-[11px] text-[#737373] uppercase tracking-wide font-mono">Owner reply · {tone}</p>
            </div>
          </div>
          <button
            onClick={() => copy(reply)}
            className="text-xs font-semibold text-[#002FA7] inline-flex items-center gap-1"
            data-testid="copy-review-reply"
          >
            <Copy size={12} weight="bold" /> Copy reply
          </button>
        </div>
        <p className="text-[15px] text-[#0A0A0A] whitespace-pre-wrap leading-relaxed">{reply}</p>
      </div>

      <div className="flex flex-wrap gap-2 text-[11px] font-mono uppercase tracking-[0.18em] text-[#737373]">
        <span className="border border-[#E5E7EB] px-2.5 py-1">Chars · {reply.length}</span>
        <span className="border border-[#E5E7EB] px-2.5 py-1">Words · {reply.split(/\s+/).filter(Boolean).length}</span>
        <span className="border border-[#E5E7EB] px-2.5 py-1 flex items-center gap-1.5">
          <XLogo size={10} weight="bold" /> / <LinkedinLogo size={10} weight="bold" /> / <InstagramLogo size={10} weight="bold" /> ready
        </span>
      </div>
    </div>
  );
}

function Group({ title, hint, children }) {
  return (
    <section>
      <div className="flex items-end justify-between mb-3">
        <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-[#0A0A0A]">— {title}</h3>
        {hint && <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373]">{hint}</span>}
      </div>
      {children}
    </section>
  );
}
